import { HEADER_MOBILE_HEIGHT } from "@src/constants";
import { useEffect, useState } from "react";

interface UseMobileHeaderOffsetParams {
  isMobile: boolean;
  isInSalesforce: boolean;
  enabled?: boolean;
}

export const useMobileHeaderOffset = ({
  isMobile,
  isInSalesforce,
  enabled = true,
}: UseMobileHeaderOffsetParams): string => {
  const [mobileHeaderOffset, setMobileHeaderOffset] = useState(HEADER_MOBILE_HEIGHT);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!isMobile || isInSalesforce) {
      setMobileHeaderOffset("0");
      return;
    }

    const getHeaderElement = () => document.querySelector<HTMLElement>(".abyss-header-root");
    let observedHeaderElement: HTMLElement | null = null;
    let mutationObservationRoot: ParentNode | null = null;
    let animationFrameId: number | null = null;

    const setOffsetIfChanged = (nextOffset: string) => {
      setMobileHeaderOffset((currentOffset) => {
        if (currentOffset === nextOffset) {
          return currentOffset;
        }

        return nextOffset;
      });
    };

    const updateHeaderOffset = () => {
      const headerElement = getHeaderElement();
      if (!headerElement) {
        setOffsetIfChanged(HEADER_MOBILE_HEIGHT);
        return;
      }

      const measuredHeight = headerElement.getBoundingClientRect().height;
      if (measuredHeight > 0) {
        // Removing 2px due to round-off logic to avoid whitespace between the header and the rest of the content on mobile view.
        setOffsetIfChanged(`${Math.ceil(measuredHeight) - 2}px`);
      }
    };

    const scheduleUpdateHeaderOffset = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = globalThis.requestAnimationFrame(() => {
        animationFrameId = null;
        updateHeaderOffset();
      });
    };

    let resizeObserver: ResizeObserver | null = null;
    const attachResizeObserver = () => {
      if (typeof ResizeObserver === "undefined") {
        return;
      }

      const headerElement = getHeaderElement();
      if (!headerElement || observedHeaderElement === headerElement) {
        return;
      }

      resizeObserver?.disconnect();
      resizeObserver = new ResizeObserver(scheduleUpdateHeaderOffset);
      resizeObserver.observe(headerElement);
      observedHeaderElement = headerElement;
    };

    let mutationObserver: MutationObserver | null = null;

    const getMutationObservationRoot = (): ParentNode => {
      const headerElement = getHeaderElement();

      // Prefer the header parent so unrelated app updates do not trigger offset recalculation.
      return headerElement?.parentElement ?? document.body;
    };

    const ensureMutationObserverAttached = () => {
      if (typeof MutationObserver === "undefined") {
        return;
      }

      if (!mutationObserver) {
        mutationObserver = new MutationObserver(() => {
          attachResizeObserver();
          ensureMutationObserverAttached();
          scheduleUpdateHeaderOffset();
        });
      }

      const nextRoot = getMutationObservationRoot();
      if (mutationObservationRoot === nextRoot) {
        return;
      }

      mutationObserver.disconnect();
      mutationObserver.observe(nextRoot, {
        childList: true,
        subtree: true,
      });
      mutationObservationRoot = nextRoot;
    };

    // Observe first so late header mount/reparent events are captured during initialization,
    // then do immediate measure and resize-observer wiring for the current DOM snapshot.
    ensureMutationObserverAttached();
    updateHeaderOffset();
    attachResizeObserver();
    globalThis.addEventListener("resize", scheduleUpdateHeaderOffset);

    return () => {
      globalThis.removeEventListener("resize", scheduleUpdateHeaderOffset);

      if (animationFrameId !== null) {
        globalThis.cancelAnimationFrame(animationFrameId);
      }

      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
    };
  }, [enabled, isMobile, isInSalesforce]);

  return mobileHeaderOffset;
};
